import { queries, type Repo, type Store } from '@wiztime/storage';
import { isZeroHlc, RecordType, type AnyEnvelope } from '@wiztime/core';
import { classifyFailure, backoffDelayMs } from './classify';
import { markFullPushPending, type SyncConfig, type SyncConfigStore } from './config';
import {
  KNOWN_RECORD_TYPES,
  LOCAL_CHANGE_DEBOUNCE_MS,
  MAX_CIPHERTEXT_CHARS,
  MAX_PUSH_RECORDS,
  PULL_PAGE_SIZE,
  SYNC_INTERVAL_MS,
  SYNC_LOCK_NAME,
} from './constants';
import { decryptRecord, encryptEnvelope, type EncryptedRecord } from './crypto';
import {
  DecryptError,
  DecryptFailure,
  ServerErrorCode,
  SyncError,
  SyncHttpError,
  SyncNotEnabledError,
  SyncProtocolError,
} from './errors';
import type { DerivedKeys } from './keys';
import {
  silentLogger,
  SyncState,
  type SyncLogger,
  type SyncRunOutcome,
  type SyncStatus,
  type SyncStatusListener,
} from './status';
import type { SyncTransport } from './transport';

export interface EventSource {
  addEventListener(type: string, listener: () => void): void;
  removeEventListener(type: string, listener: () => void): void;
}

export interface SyncEngineOptions {
  readonly store: Store;
  /** The repository to read the outbox from; defaults to `store.repo`. */
  readonly repo?: Repo;
  readonly keys: DerivedKeys;
  readonly transport: SyncTransport;
  /** Device-local configuration (`wiztime.sync`); it must exist and belong to `keys.spaceId`. */
  readonly storage: SyncConfigStore;
  /** Epoch milliseconds; defaults to `Date.now`. */
  readonly now?: () => number;
  readonly logger?: SyncLogger;
  /** Random numbers in [0, 1) for backoff jitter. */
  readonly random?: () => number;
  /** Runs one sync at a time across tabs of the same browser profile (Web Locks by default). */
  readonly lock?: <T>(task: () => Promise<T>) => Promise<T>;
  /** Source of `focus` and `online` events; defaults to `window` when it exists. */
  readonly events?: EventSource | null;
  /** Called after a pull changed local records, with their identifiers. */
  readonly onRemoteApplied?: (changedIds: readonly string[]) => void;
  readonly debounceMs?: number;
  readonly intervalMs?: number;
  /** Records requested per pull page; defaults to `PULL_PAGE_SIZE`. Tests lower it to get several pages from a few records. */
  readonly pullPageSize?: number;
  /**
   * The record types this build understands; defaults to `KNOWN_RECORD_TYPES`. Tests lower or raise it to
   * simulate an older or newer build.
   */
  readonly knownRecordTypes?: readonly string[];
}

export interface SyncNowOptions {
  /**
   * Re-pull everything from sequence 0 and push every local record, not only the new ones.
   * Use it to repair a device after a restore or when records were skipped.
   */
  readonly full?: boolean;
  /**
   * Re-pull everything from sequence 0 without touching the push state (nothing is uploaded again).
   * Records already held are merged idempotently. Use it to fetch records an earlier run skipped.
   */
  readonly repull?: boolean;
}

export interface SyncEngine {
  /** Begin automatic syncing (triggers) and run a first sync. No-op when already started. */
  start(): void;
  /** Stop automatic syncing; resolves when a run in progress has ended. */
  stop(): Promise<void>;
  /** Run a sync now (single-flight: a call during a run waits for it and then runs once more). Never rejects. */
  syncNow(options?: SyncNowOptions): Promise<SyncRunOutcome>;
  /**
   * Answer the `SpaceMissing` state: create the space again on the server (the passphrase's own
   * token), start from cursor 0 and push every local record. Same guarantees as `syncNow`.
   */
  reuploadSpace(): Promise<SyncRunOutcome>;
  /** Tell the engine a local change happened that the store did not announce; schedules a debounced sync. */
  notifyLocalChange(): void;
  /**
   * Mark every local record for pushing at the next sync. Call it after `store.importExport`, whose
   * records keep their old timestamps and would otherwise stay below the outbox watermark.
   */
  requestFullPush(): void;
  getStatus(): SyncStatus;
  subscribe(listener: SyncStatusListener): () => void;
}

interface Counters {
  pushed: number;
  pulled: number;
  applied: number;
  skipped: number;
}

/** Thrown inside a run when `stop()` was called, to unwind without recording a failure. */
class RunCancelled extends SyncError {}

/** Thrown inside a run when a pull showed that the space was created again; the run starts over. */
class EpochChanged extends SyncError {}

/** More restarts than this in one run mean the server keeps changing its mind: give up. */
const MAX_EPOCH_RESTARTS = 3;

interface RunRequest {
  /** Re-pull from cursor 0 and push every local record. */
  readonly full: boolean;
  /** Re-pull from cursor 0, leaving the push state alone. */
  readonly repull: boolean;
  /** Create the space again first (implies a full push from cursor 0). */
  readonly reupload: boolean;
}

const PLAIN_RUN: RunRequest = { full: false, repull: false, reupload: false };

/** The newest hlc this engine pushed and the records pushed at exactly that hlc. */
interface PushBoundary {
  readonly hlc: string;
  readonly ids: ReadonlySet<string>;
}

const NO_BOUNDARY: PushBoundary = { hlc: '', ids: new Set() };

const SLICE = MAX_PUSH_RECORDS;

/**
 * Runs a task under the Web Lock that serialises sync cycles of all tabs of this browser profile. Anything that
 * deletes local data must run under it, so it cannot interleave with a pull that is about to write records.
 */
export function withSyncLock<T>(task: () => Promise<T>): Promise<T> {
  const locks = typeof navigator === 'undefined' ? undefined : navigator.locks;
  return locks === undefined ? task() : locks.request(SYNC_LOCK_NAME, task);
}

function defaultEvents(): EventSource | null {
  return typeof window === 'undefined' ? null : window;
}

function sameTypes(a: readonly string[], b: readonly string[]): boolean {
  return a.length === b.length && a.every((type, index) => type === b[index]);
}

function sameStatus(a: SyncStatus, b: SyncStatus): boolean {
  return (
    a.state === b.state &&
    a.lastSyncAt === b.lastSyncAt &&
    a.lastError === b.lastError &&
    a.skippedRecords === b.skippedRecords &&
    a.retryAt === b.retryAt
  );
}

export function createSyncEngine(options: SyncEngineOptions): SyncEngine {
  const { store, keys, transport, storage } = options;
  const repo = options.repo ?? store.repo;
  const now = options.now ?? (() => Date.now());
  const logger = options.logger ?? silentLogger;
  const random = options.random ?? Math.random;
  const lock = options.lock ?? withSyncLock;
  const debounceMs = options.debounceMs ?? LOCAL_CHANGE_DEBOUNCE_MS;
  const intervalMs = options.intervalMs ?? SYNC_INTERVAL_MS;
  const ownDeviceId = store.deviceId;
  const pullPageSize = options.pullPageSize ?? PULL_PAGE_SIZE;
  const knownTypes = [...(options.knownRecordTypes ?? KNOWN_RECORD_TYPES)].sort();
  const knownTypeSet: ReadonlySet<string> = new Set(knownTypes);
  const typesKey = knownTypes.join(',');
  /** `typesKey` once this tab pulled again from cursor 0 for its type set (see `resumeAfterUpgrade`). */
  let rewoundFor: string | null = null;

  const initial = storage.load();
  if (initial !== null && initial.spaceId !== keys.spaceId) {
    throw new SyncError('The stored sync configuration belongs to a different space than the keys');
  }

  let status: SyncStatus = {
    state: SyncState.Disabled,
    lastSyncAt: initial?.lastSyncAt ?? null,
    lastError: null,
    skippedRecords: 0,
    retryAt: null,
  };
  const listeners = new Set<SyncStatusListener>();

  /** Automatic triggers are on (between `start` and `stop`). */
  let active = false;
  /** `stop` was called: runs in progress unwind and new runs are refused until `start`. */
  let stopped = false;
  /** Authentication failed: automatic triggers do nothing until the user calls `syncNow`. */
  let paused = false;
  let failures = 0;
  let backoffUntil = 0;
  /**
   * Local records may have changed since the last outbox scan. Set by store notifications and
   * explicit syncs, so the periodic sync does not re-read the whole local database every minute.
   */
  let dirty = true;
  let running: Promise<SyncRunOutcome> | null = null;
  let queued: Promise<SyncRunOutcome> | null = null;
  let queuedRequest: RunRequest = PLAIN_RUN;
  /**
   * Another tab of this device can write a different record with exactly the hlc of the last one
   * pushed (same device id, same millisecond and counter), so the outbox reads from the watermark
   * inclusively. This remembers what was pushed at the watermark so the next sync does not send
   * it again; after a restart the boundary records are sent once more and the server answers stale.
   */
  let boundary: PushBoundary = NO_BOUNDARY;
  /**
   * Timestamps of the records the space delivered during the pull-first phase of this run. The full push that
   * follows leaves out every record with one of them: the space holds those already (a block cut at a
   * day boundary keeps the timestamp of its source, so the pieces derived from a received block are covered too).
   * Only records this device holds and the space lacks, and newer local edits, go up. Memory only: after a
   * reload the push may send some of them again, which the server answers as stale.
   */
  let firstPushSkip: ReadonlySet<string> | null = null;

  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let intervalTimer: ReturnType<typeof setInterval> | null = null;
  let retryTimer: ReturnType<typeof setTimeout> | null = null;
  let unsubscribeStore: (() => void) | null = null;
  let detachEvents: (() => void) | null = null;

  function setStatus(patch: Partial<SyncStatus>): void {
    const next = { ...status, ...patch };
    if (sameStatus(status, next)) return;
    status = next;
    for (const listener of [...listeners]) listener(status);
  }

  function readConfig(): SyncConfig {
    const config = storage.load();
    if (config === null) throw new SyncNotEnabledError();
    return config;
  }

  function updateConfig(patch: Partial<SyncConfig>): SyncConfig {
    const next = { ...readConfig(), ...patch };
    storage.save(next);
    return next;
  }

  function assertActive(): void {
    if (stopped) throw new RunCancelled('The sync engine was stopped');
  }

  function clearRetryTimer(): void {
    if (retryTimer !== null) clearTimeout(retryTimer);
    retryTimer = null;
  }

  // --- Push -----------------------------------------------------------------------------------

  /**
   * The outbox, ordered by hlc. Two kinds of records never leave the device:
   * - Records with a zero or system hlc (generated drafts, default projects, purge tombstones,
   *   all with a millisecond part of 0). Every device derives them identically from its own
   *   data under deterministic identifiers, and any user edit outranks them.
   * - Records merged from the server or an import keep their author's device id, so they are
   *   not pushed back by the incremental rule below; that is what keeps two devices from
   *   echoing records to each other.
   *
   * Incremental: records this device authored (user edits, undo and redo carry its device id)
   * with an hlc at or above the watermark. Full: every record at or above the resume point,
   * whoever authored it (first sync, import, space created again, local database replaced). "At or above"
   * because two tabs of one device can write different records with the same hlc; the ones this
   * engine already pushed at the boundary are left out (see `boundary`).
   */
  async function loadOutbox(config: SyncConfig): Promise<{ records: AnyEnvelope[]; full: boolean }> {
    const full = config.fullPushFrom !== null;
    const from = config.fullPushFrom ?? config.lastPushedHlc;
    const skip = firstPushSkip;
    const since = (await queries.recordsSince(repo, from, { inclusive: true })).filter(
      (record) =>
        !(record.hlc === boundary.hlc && boundary.ids.has(record.id)) &&
        !(skip !== null && skip.has(record.hlc)),
    );
    return {
      records: since.filter((record) => !isZeroHlc(record.hlc) && (full || record.deviceId === ownDeviceId)),
      full,
    };
  }

  function rememberPushed(slice: readonly AnyEnvelope[]): void {
    for (const record of slice) {
      if (record.hlc > boundary.hlc) boundary = { hlc: record.hlc, ids: new Set([record.id]) };
      else if (record.hlc === boundary.hlc)
        boundary = { hlc: boundary.hlc, ids: new Set([...boundary.ids, record.id]) };
    }
  }

  /** Push everything again from the start; `patch` resets whatever else the cause invalidated. */
  function startFullPush(patch: Partial<SyncConfig>): void {
    boundary = NO_BOUNDARY;
    // Pulling from 0 with this build fetches what earlier builds skipped as unknown, so the marker is current.
    updateConfig({ ...patch, fullPushFrom: '', knownTypes });
  }

  async function sealSlice(slice: readonly AnyEnvelope[], counters: Counters): Promise<EncryptedRecord[]> {
    const sealed = await Promise.all(slice.map((envelope) => encryptEnvelope(keys, keys.spaceId, envelope)));
    return sealed.filter((record) => {
      if (record.ciphertext.length <= MAX_CIPHERTEXT_CHARS) return true;
      counters.skipped += 1;
      logger.warn('record too large for the server, not pushed', { rid: record.rid.slice(0, 8) });
      return false;
    });
  }

  async function pushPhase(counters: Counters): Promise<void> {
    const config = readConfig();
    if (!dirty && config.fullPushFrom === null) return;
    dirty = false;
    const { records, full } = await loadOutbox(config);
    let watermark = config.lastPushedHlc;
    for (let offset = 0; offset < records.length; offset += SLICE) {
      assertActive();
      const slice = records.slice(offset, offset + SLICE);
      const sealed = await sealSlice(slice, counters);
      if (sealed.length > 0) {
        const result = await transport.push(sealed);
        counters.pushed += result.accepted.length;
      }
      rememberPushed(slice);
      // Records are ordered by hlc: everything up to the slice's last record has now been pushed.
      for (const record of slice) {
        if (record.deviceId === ownDeviceId && record.hlc > watermark) watermark = record.hlc;
      }
      const lastHlc = slice[slice.length - 1]?.hlc ?? '';
      updateConfig(full ? { fullPushFrom: lastHlc, lastPushedHlc: watermark } : { lastPushedHlc: watermark });
    }
    if (full) updateConfig({ fullPushFrom: null, lastPushedHlc: watermark });
  }

  // --- Pull -----------------------------------------------------------------------------------

  /**
   * Compare the record types this build knows with the marker in the configuration (`knownTypes`).
   * - No marker (written by a build without it) or a marker that is a strict subset of this build's types:
   *   builds that knew fewer types skipped records of the others and moved the cursor on, so pull again
   *   from cursor 0 (the push state is not touched; merging a record again has no effect), then store the
   *   current types. Nothing is pulled again when the cursor is 0 already, or when this configuration was
   *   already pulled again for exactly this type set (`rewoundTypes`, `rewoundFor`): an older tab that
   *   keeps overwriting the marker with its smaller set must not make this tab pull again and again.
   * - A marker that is equal, or lists types this build does not know (a newer build wrote it): left alone.
   * `rewoundFor` is the same memory kept in this tab: an older build drops `rewoundTypes` when it saves.
   */
  function resumeAfterUpgrade(): void {
    const config = readConfig();
    const marker = config.knownTypes;
    const strictSubset =
      marker !== null && marker.length < knownTypes.length && marker.every((type) => knownTypeSet.has(type));
    if (marker !== null && !strictSubset) return;
    const alreadyRewound = rewoundFor === typesKey || (config.rewoundTypes !== null && sameTypes(config.rewoundTypes, knownTypes));
    if (config.cursor === 0 || alreadyRewound) {
      updateConfig({ knownTypes });
      return;
    }
    logger.info('pulling again from the start: this build knows more record types', {
      previousCursor: config.cursor,
      previousTypes: marker,
    });
    rewoundFor = typesKey;
    updateConfig({ cursor: 0, knownTypes, rewoundTypes: knownTypes });
  }

  async function decryptPage(
    page: readonly EncryptedRecord[],
    counters: Counters,
    startCursor: number,
  ): Promise<AnyEnvelope[]> {
    const decrypted = await Promise.all(
      page.map(async (record) => {
        try {
          return await decryptRecord(keys, keys.spaceId, record, knownTypeSet);
        } catch (error) {
          if (!(error instanceof DecryptError)) throw error;
          counters.skipped += 1;
          if (error.reason === DecryptFailure.UnknownRecordType) {
            logger.warn('record skipped: type unknown to this build', {
              rid: record.rid.slice(0, 8),
              cursor: startCursor,
            });
            return null;
          }
          logger.warn('record skipped: cannot be decrypted', {
            rid: record.rid.slice(0, 8),
            reason: error.reason,
          });
          return null;
        }
      }),
    );
    return decrypted.filter((envelope): envelope is AnyEnvelope => envelope !== null);
  }

  /**
   * Compare the epoch of a server answer with the stored one. A different epoch means the space was
   * deleted and created again: the cursor belongs to the old incarnation, so start over from 0 and
   * push everything (the new space may lack records this device holds). Throws `EpochChanged`.
   */
  function checkEpoch(epoch: string): void {
    const stored = readConfig().epoch;
    if (stored === epoch) return;
    if (stored === '') {
      updateConfig({ epoch }); // a configuration from before epochs: nothing to compare with
      return;
    }
    logger.warn('the space was created again on the server: pulling everything again and pushing everything');
    startFullPush({ cursor: 0, epoch });
    throw new EpochChanged('The space on the server is a new one');
  }

  /**
   * @param firstSync the first sync of a device that joined an existing space: a setting the space holds
   *   replaces the device's own whatever the timestamps say, nothing is re-homed page by page (the day start is
   *   known only after the last page), and the timestamps of the delivered records are collected.
   */
  async function pullPhase(
    counters: Counters,
    changed: string[],
    firstSync: Set<string> | null = null,
  ): Promise<void> {
    for (;;) {
      assertActive();
      const { cursor } = readConfig();
      const page = await transport.pull(cursor, pullPageSize);
      checkEpoch(page.epoch);
      if (page.cursor < cursor || (page.hasMore && (page.records.length === 0 || page.cursor <= cursor))) {
        throw new SyncProtocolError('The server returned a cursor that does not advance');
      }
      const envelopes = await decryptPage(page.records, counters, cursor);
      if (envelopes.length > 0) {
        // Another tab may have logged out, switched account or cleared the data while this page was in flight:
        // writing now would leave records of the old space on a device that no longer remembers them.
        const current = storage.load();
        if (current === null || current.spaceId !== keys.spaceId) {
          logger.warn('pulled page discarded: the sync configuration changed during the pull');
          throw new RunCancelled('The sync configuration changed during the pull');
        }
        if (firstSync !== null) for (const envelope of envelopes) firstSync.add(envelope.hlc);
        const merge = await store.applyRemote(
          envelopes,
          firstSync === null ? undefined : { remoteWinsTypes: [RecordType.Setting], deferRehome: true },
        );
        counters.applied += merge.changedIds.length;
        counters.skipped += merge.rejected.length;
        changed.push(...merge.changedIds);
        if (merge.rejected.length > 0) {
          // Invalid after decryption (written by a newer client, say) or more than 24 hours ahead
          // of this device's clock. They are not retried: the cursor moves on.
          logger.warn('records skipped: rejected by the local store', {
            count: merge.rejected.length,
            issues: [...new Set(merge.rejected.flatMap((rejected) => rejected.issues))].slice(0, 5),
          });
        }
      }
      counters.pulled += page.records.length;
      // Saved only now: the merge above is committed, so a crash re-pulls this page at worst.
      updateConfig({ cursor: page.cursor });
      if (!page.hasMore) return;
    }
  }

  // --- One run --------------------------------------------------------------------------------

  /**
   * A deleted or replaced local database leaves a cursor (and push watermark) that refers to data
   * this database no longer holds: it would skip everything. Every database carries a random
   * identity (`Store.getDbInstanceId`); the configuration remembers the one it was synced with.
   */
  async function guardAgainstReplacedDatabase(): Promise<void> {
    const current = await store.getDbInstanceId();
    const config = readConfig();
    if (config.dbInstanceId === current) return;
    if (config.dbInstanceId === '') {
      updateConfig({ dbInstanceId: current }); // a configuration from before database identities
      return;
    }
    logger.warn('the local database was replaced: pulling everything again and pushing everything');
    startFullPush({ cursor: 0, lastPushedHlc: '', dbInstanceId: current });
  }

  /** The space is missing and the user asked to create it again with this device's data. */
  async function createSpaceAgain(): Promise<void> {
    const { epoch } = await transport.putSpace();
    logger.info('the space was created again at the user request: pushing all records');
    startFullPush({ cursor: 0, epoch });
  }

  /**
   * A refused push that does not forbid reading: "the space is full" or "the email is not verified yet".
   * The pull still runs and the refusal is reported afterwards.
   */
  function isPushRefusal(error: unknown): error is SyncHttpError {
    return (
      error instanceof SyncHttpError &&
      (error.code === ServerErrorCode.QuotaExceeded ||
        (error.status === 403 && error.code === ServerErrorCode.EmailUnverified))
    );
  }

  /** A pull completed although the push was refused: that pull is a sync of its own. */
  function recordPull(): void {
    const finishedAt = now();
    updateConfig({ lastSyncAt: finishedAt });
    setStatus({ lastSyncAt: finishedAt });
  }

  async function runSteps(request: RunRequest, counters: Counters, changed: string[]): Promise<void> {
    assertActive();
    await guardAgainstReplacedDatabase();
    if (request.reupload) await createSpaceAgain();
    else if (request.full) startFullPush({ cursor: 0 });
    else if (request.repull) {
      logger.info('pulling everything again at the user request');
      updateConfig({ cursor: 0, knownTypes });
    } else resumeAfterUpgrade();
    // A missing space (404) is not repaired here: it surfaces as `SyncState.SpaceMissing` and the
    // user decides, because another device may have deleted the cloud data on purpose.
    for (let restarts = 0; ; restarts += 1) {
      if (readConfig().pullFirst) {
        // A device that joined an existing space reads it before it writes anything: the space's settings
        // (day start included) replace the device's own, a setting the space lacks falls back to its default,
        // and blocks and templates are re-homed once, after the last page, under the final day start. None of
        // that is pushed back (docs/time-drift.md D6, architecture-account.md section 2.7).
        const received = new Set<string>();
        try {
          if (readConfig().cursor === 0) changed.push(...(await store.resetSettingsToDefaults()));
          await pullPhase(counters, changed, received);
          assertActive();
          changed.push(...(await store.rehomeAllToDayStart()));
          updateConfig({ pullFirst: false });
          firstPushSkip = received;
        } catch (error) {
          if (!(error instanceof EpochChanged)) throw error;
          if (restarts >= MAX_EPOCH_RESTARTS)
            throw new SyncProtocolError('The space keeps changing on the server');
          continue;
        }
      }
      let refusedPush: SyncHttpError | null = null;
      try {
        await pushPhase(counters);
      } catch (error) {
        dirty = true;
        // A full space or an unverified account refuses new records but must still deliver other devices' records.
        if (isPushRefusal(error)) refusedPush = error;
        else throw error;
      } finally {
        firstPushSkip = null;
      }
      try {
        await pullPhase(counters, changed);
      } catch (error) {
        if (!(error instanceof EpochChanged)) throw error;
        if (restarts >= MAX_EPOCH_RESTARTS)
          throw new SyncProtocolError('The space keeps changing on the server');
        continue; // the configuration was reset: push everything to the new space, then pull from 0
      }
      if (refusedPush !== null) {
        recordPull();
        throw refusedPush;
      }
      return;
    }
  }

  function succeed(counters: Counters, startedAt: number): SyncRunOutcome {
    failures = 0;
    backoffUntil = 0;
    clearRetryTimer();
    const finishedAt = now();
    updateConfig({ lastSyncAt: finishedAt });
    setStatus({
      state: SyncState.Idle,
      lastSyncAt: finishedAt,
      lastError: null,
      retryAt: null,
      skippedRecords: status.skippedRecords + counters.skipped,
    });
    logger.info('sync completed', { ...counters, durationMs: finishedAt - startedAt });
    return { ok: true, ...counters };
  }

  function fail(error: unknown, counters: Counters, startedAt: number): SyncRunOutcome {
    if (error instanceof RunCancelled) return { ok: false, error: null, state: SyncState.Disabled };
    if (error instanceof SyncNotEnabledError) {
      setStatus({ state: SyncState.Disabled, retryAt: null });
      return { ok: false, error: null, state: SyncState.Disabled };
    }
    const failure = classifyFailure(error);
    failures += 1;
    paused = failure.pause;
    clearRetryTimer();
    let retryAt: number | null = null;
    if (!failure.pause) {
      const delay = backoffDelayMs(failures, failure.retryAfter, random);
      backoffUntil = now() + delay;
      retryAt = backoffUntil;
      retryTimer = setTimeout(() => {
        retryTimer = null;
        if (active && !paused) void schedule(PLAIN_RUN);
      }, delay);
    }
    setStatus({
      state: failure.state,
      lastError: failure.code,
      retryAt,
      skippedRecords: status.skippedRecords + counters.skipped,
    });
    logger.warn('sync failed', {
      code: failure.code,
      httpStatus: error instanceof SyncHttpError ? error.status : undefined,
      retryInMs: retryAt === null ? undefined : retryAt - now(),
      durationMs: now() - startedAt,
    });
    return { ok: false, error: failure.code, state: failure.state };
  }

  function notifyApplied(changed: readonly string[]): void {
    if (changed.length > 0) options.onRemoteApplied?.(changed);
  }

  async function execute(request: RunRequest): Promise<SyncRunOutcome> {
    const startedAt = now();
    const counters: Counters = { pushed: 0, pulled: 0, applied: 0, skipped: 0 };
    const changed: string[] = [];
    if (stopped) return { ok: false, error: null, state: SyncState.Disabled };
    setStatus({ state: SyncState.Syncing });
    try {
      await lock(() => runSteps(request, counters, changed));
    } catch (error) {
      // Pages merged before the failure are in the store whatever happened next.
      notifyApplied(changed);
      return fail(error, counters, startedAt);
    }
    notifyApplied(changed);
    return succeed(counters, startedAt);
  }

  function startRun(request: RunRequest): Promise<SyncRunOutcome> {
    const run: Promise<SyncRunOutcome> = execute(request).finally(() => {
      if (running === run) running = null;
    });
    running = run;
    return run;
  }

  /** Run now, or once after the run in progress; concurrent requests share the follow-up run. */
  function schedule(request: RunRequest): Promise<SyncRunOutcome> {
    if (running === null) return startRun(request);
    queuedRequest = {
      full: queuedRequest.full || request.full,
      repull: queuedRequest.repull || request.repull,
      reupload: queuedRequest.reupload || request.reupload,
    };
    queued ??= running.then(() => {
      const wanted = queuedRequest;
      queuedRequest = PLAIN_RUN;
      queued = null;
      return startRun(wanted);
    });
    return queued;
  }

  function syncNow(syncOptions: SyncNowOptions = {}): Promise<SyncRunOutcome> {
    paused = false;
    dirty = true;
    return schedule({
      full: syncOptions.full === true,
      repull: syncOptions.repull === true,
      reupload: false,
    });
  }

  function reuploadSpace(): Promise<SyncRunOutcome> {
    paused = false;
    dirty = true;
    return schedule({ full: false, repull: false, reupload: true });
  }

  // --- Triggers -------------------------------------------------------------------------------

  /** Automatic trigger: ignores the request while stopped, paused after an authentication failure, or backing off. */
  function trigger(): void {
    if (!active || paused) return;
    if (now() < backoffUntil) return;
    void schedule(PLAIN_RUN);
  }

  function notifyLocalChange(): void {
    dirty = true;
    if (!active) return;
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      trigger();
    }, debounceMs);
  }

  function onOnline(): void {
    // The network is back: end the network backoff, but keep a server-requested wait.
    if (status.state === SyncState.Offline) {
      backoffUntil = 0;
      failures = 0;
    }
    trigger();
  }

  function start(): void {
    if (active) return;
    if (storage.load() === null) {
      setStatus({ state: SyncState.Disabled });
      return;
    }
    active = true;
    stopped = false;
    paused = false;
    setStatus({ state: SyncState.Idle, lastError: null });
    unsubscribeStore = store.subscribe(notifyLocalChange);
    const events = options.events === undefined ? defaultEvents() : options.events;
    if (events !== null) {
      const onFocus = (): void => trigger();
      events.addEventListener('focus', onFocus);
      events.addEventListener('online', onOnline);
      detachEvents = () => {
        events.removeEventListener('focus', onFocus);
        events.removeEventListener('online', onOnline);
      };
    }
    intervalTimer = setInterval(() => trigger(), intervalMs);
    dirty = true;
    void schedule(PLAIN_RUN);
  }

  async function stop(): Promise<void> {
    active = false;
    stopped = true;
    if (debounceTimer !== null) clearTimeout(debounceTimer);
    debounceTimer = null;
    if (intervalTimer !== null) clearInterval(intervalTimer);
    intervalTimer = null;
    clearRetryTimer();
    unsubscribeStore?.();
    unsubscribeStore = null;
    detachEvents?.();
    detachEvents = null;
    await queued;
    await running;
    setStatus({ state: SyncState.Disabled, retryAt: null });
  }

  return {
    start,
    stop,
    syncNow,
    reuploadSpace,
    notifyLocalChange,
    requestFullPush() {
      boundary = NO_BOUNDARY;
      markFullPushPending(storage);
    },
    getStatus: () => status,
    subscribe(listener) {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
}
